import React from 'react';
import { UseFormReturn } from 'react-hook-form';

import { StrategyType } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { cn } from '@/utils/cn';

import { FormValues } from './strategy-form-schema';
import { StrategyAssetTable } from './StrategyAssetTable';
import { StrategyExposurePanel } from './StrategyExposurePanel';

const TYPE_OPTIONS: Array<{ value: StrategyType.PERSONAL | StrategyType.SHARED; label: string }> = [
    { value: StrategyType.PERSONAL, label: 'Personal' },
    { value: StrategyType.SHARED, label: 'Shared' },
];

interface StrategyFormBodyProps {
    form: UseFormReturn<FormValues>;
    /** Whether the Personal/Shared toggle renders at all (e.g. hidden entirely for non-support users). */
    showTypeSelector: boolean;
    /** Whether the toggle is interactive — e.g. type can't change once a strategy already exists. */
    canChangeType: boolean;
    getAssetInfo: (securityId: string) => AssetInfo | undefined;
    showAddAsset: boolean;
    onShowAddAsset: () => void;
    onCancelAdd: () => void;
    onAddAsset: (asset: AssetInfo) => void;
    onUpdateWeight: (securityId: string, weight: string) => void;
    onRemoveAsset: (securityId: string) => void;
    onAddReplacement: (asset: AssetInfo) => void;
    onRemoveReplacement: (assetSecurityId: string, replacementSecurityId: string) => void;
    addingReplacementForAsset: string | null;
    setAddingReplacementForAsset: (securityId: string | null) => void;
    onEvenSplit: () => void;
    /** Ref to the name input — used by callers (e.g. a dialog re-opening) that need to focus it imperatively. */
    nameInputRef?: { current: HTMLInputElement | null };
    /** Native autofocus — only safe for callers that mount fresh once (e.g. a page navigation), not a dialog that toggles visibility. */
    autoFocusName?: boolean;
    readOnly?: boolean;
    /** Replaces the holdings table and exposure panel for strategy types with custom details. */
    assetDetailsOverride?: React.ReactNode;
}

export const StrategyFormBody: React.FC<StrategyFormBodyProps> = ({
    form,
    showTypeSelector,
    canChangeType,
    getAssetInfo,
    showAddAsset,
    onShowAddAsset,
    onCancelAdd,
    onAddAsset,
    onUpdateWeight,
    onRemoveAsset,
    onAddReplacement,
    onRemoveReplacement,
    addingReplacementForAsset,
    setAddingReplacementForAsset,
    onEvenSplit,
    nameInputRef,
    autoFocusName,
    readOnly = false,
    assetDetailsOverride,
}) => {
    const strategyType = form.watch('strategyType');
    const assetAllocations = form.watch('assetAllocations');
    const { ref: registerNameRef, ...nameFieldProps } = form.register('strategyName');

    return (
        <div className="flex flex-wrap items-start gap-6">
            <div className="flex min-w-[480px] flex-1 flex-col gap-5">
                <div className="border-cocoa-200 flex flex-col gap-3.5 rounded-lg border bg-white p-6">
                    <label htmlFor="strategy-name" className="text-cocoa-500 text-xs">
                        Strategy name
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            id="strategy-name"
                            {...nameFieldProps}
                            ref={(el) => {
                                registerNameRef(el);
                                if (nameInputRef) nameInputRef.current = el;
                            }}
                            placeholder="Name this strategy"
                            autoFocus={autoFocusName}
                            readOnly={readOnly}
                            className={cn(
                                'font-heading border-cocoa-200 text-cocoa-1000 placeholder:text-cocoa-300 min-w-[220px] flex-1 border-0 border-b bg-transparent pb-0.5 text-[28px] leading-[1.4] font-normal tracking-[-0.02em] outline-none',
                                readOnly && 'cursor-default',
                            )}
                        />
                        {showTypeSelector && !readOnly && (
                            <div className="flex gap-1.5">
                                {TYPE_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        disabled={!canChangeType}
                                        onClick={() => form.setValue('strategyType', option.value)}
                                        className={cn(
                                            'rounded px-2 py-1.5 text-xs font-semibold tracking-wide uppercase',
                                            strategyType === option.value
                                                ? 'bg-cocoa-900 text-white'
                                                : 'bg-cocoa-0 text-cocoa-500',
                                            canChangeType ? 'hover:bg-cocoa-100' : 'cursor-not-allowed opacity-50',
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {form.formState.errors.strategyName && (
                        <p className="text-sm text-red-600">{form.formState.errors.strategyName.message}</p>
                    )}
                </div>

                {assetDetailsOverride ?? (
                    <StrategyAssetTable
                        assetAllocations={assetAllocations}
                        getAssetInfo={getAssetInfo}
                        errors={form.formState.errors}
                        onUpdateWeight={onUpdateWeight}
                        onRemoveAsset={onRemoveAsset}
                        onAddReplacement={onAddReplacement}
                        onRemoveReplacement={onRemoveReplacement}
                        addingReplacementForAsset={addingReplacementForAsset}
                        setAddingReplacementForAsset={setAddingReplacementForAsset}
                        showAddAsset={showAddAsset}
                        onAddAsset={onAddAsset}
                        onShowAddAsset={onShowAddAsset}
                        onCancelAdd={onCancelAdd}
                        onEvenSplit={onEvenSplit}
                        readOnly={readOnly}
                    />
                )}
            </div>

            {assetDetailsOverride === undefined && (
                <div className="w-full min-w-[260px] flex-1 sm:max-w-[340px]">
                    <StrategyExposurePanel assetAllocations={assetAllocations} />
                </div>
            )}
        </div>
    );
};
